import ExplainDialog from '@/app/_lens-components/components/ExplainDialog';
import LensTypePrice from '@/app/_lens-components/components/LensTypePrice';
import { LENS_COLOR } from '@/app/_lens-components/constants';
import { useLensProcessStore } from '@/app/_lens-components/store/lens-process-store';
import stepCommonClassName from '@/app/_lens-components/style/step-common';
import { resolveLensTypeTrackingFields } from '@/app/_lens-components/tracking';
import {
  getFlattenedLensColorCategoryColors,
  getLensColorCategoryByTitle,
  getLensColorCategoryViewModels,
  resolveLensColorCategoryClickColor,
  shouldShowLensColorCategories,
} from '@/app/_lens-components/utils/lensColorCategory';
import {
  BrandItem,
  ColorItem,
  LensColorCategory,
  LensShowItem,
  LensShowResponse,
} from '@/app/_lens-components/types';
import {
  getApiPrescriptionType,
  handleColorItemPriceInfo,
  isSameLensBrand,
  resolveBrandPriceColorSource,
  resolveOptionPromotionDisplayInfo,
  resolvePromotionDisplayInfo,
  resolveSelectablePromotionDisplayInfo,
} from '@/app/_lens-components/utils';
import {
  getLensTypeKey,
  rememberLensBrandSelection,
  resolveDefaultLensBrand,
} from '@/app/_lens-components/utils/applyDefaultLensType';
import {
  encodePrevLensTypeSelection,
  mergePrevLensSelection,
} from '@/app/_lens-components/utils/operatePrevLensSelection';
import { useDefaultSelection } from '@/app/_lens-components/DefaultSelectionContext';
import { sensorParseString } from '@/utils/sensor-parse';
import AccessiblePrice from '@/components/AccessiblePrice';
import Button from '@/components/Button';
import QuestionResponsive from '@/components/IconResponsive/QuestionResponsive';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import ProductImageWithLenses from '@/components/Product/ProductImageWithLenses';
import Tooltip from '@/components/Tooltip';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { EPrescriptionType } from '@/utils/enums/goods';
import { processLineBreaks } from '@/utils/tools';
import { Skeleton, useMediaQuery } from '@mui/material';
import { CloseOutlined, CollapseArrowDownOutlined, RightArrowOutlined } from '@zeelool/icon';
import { useTranslations } from '@/i18n/use-translations';
import { useOpticalProcedureTitle } from '@/app/_lens-components/copywriting/useOpticalProcedureTitle';
import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { NON_PRESCRIPTION_TYPES } from '@/utils/constants';

interface LensTypeProps {
  isFrameProcess: boolean;
  lensTypeColorData: LensShowResponse;
  selectedLensType?: LensShowItem;
  selectedBrand?: BrandItem;
  selectedColorItem?: ColorItem;
  onSelectedLensType?: (lensType: LensShowItem) => void;
  onSelectedBrand?: (brand: BrandItem) => void;
  onSelectedColorItem?: (colorItem: ColorItem) => void;
  onBackToEditPrescription?: () => void;
}

// 镜片类型曝光追踪组件
const LensTypeExpTracker: React.FC<{
  item: LensShowItem;
  onExposure: (colorItem: ColorItem | null, lens: LensShowItem) => void;
  exposedItems: React.MutableRefObject<Set<string>>;
}> = ({ item, onExposure, exposedItems }) => {
  const [ref, inView] = useInView({
    threshold: 0.5, // 50%的元素可见时触发
    triggerOnce: false, // 允许检测，但用 ref 控制曝光逻辑
  });

  useEffect(() => {
    if (inView) {
      // 生成镜片类型的唯一标识
      const itemKey = `${item.lens_type}_${item.lens_name}`;

      // 只有未曝光过的项目才触发曝光事件
      if (!exposedItems.current.has(itemKey)) {
        exposedItems.current.add(itemKey);

        // 获取默认颜色项目
        let defaultColorItem = null;
        if (item.merge_brand === 2) {
          // 非品牌类型，使用镜片类型的默认颜色
          if (item.default_lens_color) {
            defaultColorItem = item.color_list?.find(
              (color) => color.lens_color === item.default_lens_color,
            );
          } else if (item.color_list?.length) {
            defaultColorItem = item.color_list[0];
          }
        }

        onExposure(defaultColorItem, item);
      }
    }
  }, [inView]);

  return <div ref={ref} className="absolute inset-0 pointer-events-none" />;
};

const LensColorRadio = ({
  colorItem,
  checked,
  id,
  name,
  imageSize,
  onSelect,
  tooltipTrigger,
}: {
  colorItem: ColorItem;
  checked: boolean;
  id: string;
  name: string;
  imageSize: number;
  onSelect: () => void;
  tooltipTrigger?: 'hover' | 'click';
}) => {
  const accessibleName = colorItem.color_name || colorItem.lens_color;
  const control =
    tooltipTrigger === 'click' ? (
      <button
        id={id}
        type="button"
        className="absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer border-0 bg-transparent p-0"
        aria-label={accessibleName}
        aria-pressed={checked}
        onClick={onSelect}
      />
    ) : (
      <input
        id={id}
        className="absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer opacity-0"
        type="radio"
        name={name}
        checked={checked}
        aria-label={accessibleName}
        onChange={onSelect}
        onClick={() => {
          if (checked) onSelect();
        }}
      />
    );

  return (
    <>
      <Image
        className="pointer-events-none"
        src={colorItem.option_img}
        alt=""
        width={imageSize}
        height={imageSize}
      />
      {tooltipTrigger ? (
        <Tooltip
          title={colorItem.explain || colorItem.color_name}
          describeChild
          trigger={tooltipTrigger}
          arrow={tooltipTrigger === 'click'}
          placement="top"
          variant="light"
          stopPropagation={false}
          sx={{ zIndex: 1500 }}
        >
          {control}
        </Tooltip>
      ) : (
        control
      )}
    </>
  );
};

// TODO 弹层的商品展示未实现。等Footer层实现了做成组件复用过来
const LensType: React.FC<LensTypeProps> = ({
  isFrameProcess,
  lensTypeColorData,
  selectedLensType,
  selectedBrand,
  selectedColorItem,
  onSelectedLensType,
  onSelectedBrand,
  onSelectedColorItem,
  onBackToEditPrescription,
}) => {
  const { canModifyPrevSelection } = useDefaultSelection();

  const persistLensTypeTriple = (
    lens: LensShowItem | null | undefined,
    brand: BrandItem | null | undefined,
    color: ColorItem | null | undefined,
  ) => {
    if (!canModifyPrevSelection || !lens) return;
    mergePrevLensSelection({
      prevLensType: encodePrevLensTypeSelection(
        getLensTypeKey(lens),
        brand?.brand_title ?? '',
        color?.lens_color ?? '',
        brand?.brand_name ?? '',
      ),
    });
  };

  const t = useTranslations('lens-process');
  const tPriceA11y = useTranslations('goodsDetail.clientPrice');
  const lensTypeGroupLabelId = useId();
  const lensTypeOptionIdPrefix = useId();
  const lensTypeRadioName = useId();
  const colorPopupTitleId = useId();
  const popupColorRadioName = useId();
  const lensTypeTitle = useOpticalProcedureTitle('lens_type', t('lensType'));
  const recommendedTitle = useOpticalProcedureTitle('recommended', t('recommendedForYou'));
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  // 占位状态，实际实现时需要替换
  const [showExplainPop, setShowExplainPop] = useState(false);
  const [explainContent, setExplainContent] = useState<{
    brand_name?: string;
    lens_name?: string;
    explain?: string;
  }>({});
  const waiMode = useStore((state) => state.waiMode);
  const {
    lensGoodsItem,
    lensGoodsSensorsData,
    prescriptionType,
    presTypeOptionName,
    hasIndexPrice,
    isLoadingLensShow,
    brandExpand,
    selectedColorCategory,
    setBrandExpand,
    setSelectedColorCategory,
    showChooseColor,
    setShowChooseColor,
    setPrescriptionType,
    setPresTypeOptionName,
  } = useLensProcessStore();
  // 1280px 跟 className 里的 xl:xxx 统一
  const isWap = useMediaQuery('(max-width:1280px)');

  // 实时处方类型。解决 react 异步set的问题。
  const presTypeRef = useRef(
    getApiPrescriptionType(prescriptionType) || EPrescriptionType.FRAME_ONLY,
  );
  const presTypeOptionNameRef = useRef(presTypeOptionName || EPrescriptionType.FRAME_ONLY);

  const handleSetPrescriptionType = useCallback(
    (item?: LensShowItem | null) => {
      if (!isFrameProcess || !item) return;

      const presType =
        item.lens_type === EPrescriptionType.FRAME_ONLY
          ? EPrescriptionType.FRAME_ONLY
          : EPrescriptionType.NON_PRESCRIPTION;

      presTypeRef.current = presType;
      presTypeOptionNameRef.current = presType;

      setPrescriptionType(presType);
      setPresTypeOptionName(presType);
    },
    [isFrameProcess, setPrescriptionType, setPresTypeOptionName],
  );

  useLayoutEffect(() => {
    const apiPrescriptionType = getApiPrescriptionType(prescriptionType);
    if (apiPrescriptionType) {
      presTypeRef.current = apiPrescriptionType;
    }
    presTypeOptionNameRef.current =
      presTypeOptionName || apiPrescriptionType || EPrescriptionType.FRAME_ONLY;
  }, [prescriptionType, presTypeOptionName]);

  useLayoutEffect(() => {
    handleSetPrescriptionType(selectedLensType);
  }, [handleSetPrescriptionType, selectedLensType]);

  // 离开页面时，关闭颜色选择层
  useEffect(() => {
    return () => {
      setShowChooseColor(false);
    };
  }, []);

  const handleSetShowChooseColor = (
    show: boolean,
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
    lensColorCategoryName = '',
  ) => {
    if (isWap) {
      setShowChooseColor(show);

      SDLensTypePageColorPopUpExp(lens, colorItem, brand, lensColorCategoryName);
    }
  };

  /*
   * 点击事件处理-----开始
   */
  const scrollLensTypeOptionToTop = (index: number) => {
    const lensTypeOption = document.querySelector<HTMLElement>(`.lens-type-option-${index}`);
    if (!lensTypeOption) return;

    window.setTimeout(() => {
      lensTypeOption.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }, 0);
  };

  // 镜片类型点击
  const onLensClick = (item: LensShowItem, index: number) => {
    onSelectedLensType(item);
    handleSetPrescriptionType(item);
    const expandKey = getLensTypeKey(item);
    if (brandExpand[expandKey]) {
      // 带有二级选项的收起都在这里处理 （目前包含品牌层、颜色分组层）
      setBrandExpand({});
      SDLensTypePageSelectClick(null, item);
    } else if (item.merge_brand === 1) {
      setSelectedColorCategory(null);
      setBrandExpand({ [expandKey]: true });
      const defaultBrand = resolveDefaultLensBrand(item);
      if (defaultBrand) {
        rememberLensBrandSelection(item, defaultBrand);
        onSelectedBrand(defaultBrand);
        let colorSel: ColorItem | null = null;
        if (defaultBrand.default_lens_color) {
          colorSel =
            defaultBrand.color_list?.find(
              (color) => color.lens_color === defaultBrand.default_lens_color,
            ) ?? null;
          onSelectedColorItem(colorSel);
        } else {
          onSelectedColorItem(null);
        }
        persistLensTypeTriple(item, defaultBrand, colorSel);
      } else {
        onSelectedBrand(null);
        onSelectedColorItem(null);
        persistLensTypeTriple(item, null, null);
      }
      scrollLensTypeOptionToTop(index);
      SDLensTypePageSelectClick(null, item);
    } else if (shouldShowLensColorCategories(item)) {
      onSelectedBrand(null);

      setBrandExpand({ [expandKey]: true });

      const _defaultColorCategory = getLensColorCategoryByTitle(item, item.default_category_title);
      if (_defaultColorCategory) {
        setSelectedColorCategory(_defaultColorCategory);
        const _selectedColorItem = getDefaultColorItem(_defaultColorCategory);
        onSelectedColorItem(_selectedColorItem);
        if (_selectedColorItem) {
          persistLensTypeTriple(item, null, _selectedColorItem);
        }
      } else {
        setSelectedColorCategory(null);
        onSelectedColorItem(null);
        persistLensTypeTriple(item, null, null);
      }
      scrollLensTypeOptionToTop(index);
      SDLensTypePageSelectClick(null, item);
    } else if (hasColorList(item)) {
      onSelectedBrand(null);

      setBrandExpand({});
      const colorItem = getDefaultColorItem(item);
      onSelectedColorItem(colorItem);
      persistLensTypeTriple(item, null, colorItem);
      setSelectedColorCategory(null);
      // 展开颜色层
      handleSetShowChooseColor(true, item, colorItem);

      SDLensTypePageSelectClick(colorItem, item);
    } else {
      onSelectedBrand(null);
      setBrandExpand({});
      setSelectedColorCategory(null);
      // 直接选中
      const soleColor = item.color_list[0];
      onSelectedColorItem(soleColor);
      persistLensTypeTriple(item, null, soleColor);
      SDLensTypePageSelectClick(soleColor, item);
    }
  };
  // 品牌点击
  const onLensBrandClick = (brandItem: BrandItem, item: LensShowItem) => {
    setSelectedColorCategory(null);
    onSelectedBrand(brandItem);
    rememberLensBrandSelection(item, brandItem);

    // 合并品牌类型，添加子品牌的lens_type到sd_lens_type字段，埋点使用
    if (item.merge_lens_type) {
      item.sd_lens_type = brandItem.lens_type;
    }
    onSelectedLensType(item);
    if (hasColorList(brandItem)) {
      let colorItem = brandItem.color_list[0];
      if (brandItem.default_lens_color) {
        colorItem = brandItem.color_list.find(
          (color) => color.lens_color === brandItem.default_lens_color,
        );
      }
      onSelectedColorItem(colorItem);
      persistLensTypeTriple(item, brandItem, colorItem);
      handleSetShowChooseColor(true, item, colorItem, brandItem);

      SDLensTypePageBrandSelectClick(item, brandItem, colorItem);
    } else {
      const firstC = brandItem.color_list[0];
      onSelectedColorItem(firstC);
      persistLensTypeTriple(item, brandItem, firstC);

      SDLensTypePageBrandSelectClick(item, brandItem, firstC);
    }
  };
  // 页面上非品牌颜色点击
  const onLensColorClick = (item: LensShowItem, colorItem: ColorItem, openColorPicker = true) => {
    setSelectedColorCategory(null);
    // 清空品牌相关信息
    onSelectedBrand(null);
    setBrandExpand({});
    item.default_lens_color = colorItem.lens_color;
    onSelectedLensType(item);
    handleSetPrescriptionType(item);
    onSelectedColorItem(colorItem);
    persistLensTypeTriple(item, null, colorItem);
    if (openColorPicker) {
      handleSetShowChooseColor(true, item, colorItem);
    }

    SDLensTypePageSelectClick(colorItem, item);
  };
  // 页面上品牌层颜色点击
  const onLensBrandColorClick = (
    colorItem: ColorItem,
    brandItem: BrandItem,
    item: LensShowItem,
  ) => {
    setSelectedColorCategory(null);
    brandItem.default_lens_color = colorItem.lens_color;

    // 合并品牌类型，添加子品牌的lens_type到sd_lens_type字段，埋点使用
    if (item.merge_lens_type) {
      item.sd_lens_type = brandItem.lens_type;
    }

    onSelectedLensType(item);
    handleSetPrescriptionType(item);
    onSelectedBrand(brandItem);
    onSelectedColorItem(colorItem);
    persistLensTypeTriple(item, brandItem, colorItem);
    handleSetShowChooseColor(true, item, colorItem, brandItem);
    rememberLensBrandSelection(item, brandItem);
    SDLensTypePageBrandSelectClick(item, brandItem, colorItem);
  };
  // 弹窗中颜色点击
  const onPopupLensColorClick = (colorItem: ColorItem) => {
    if (selectedLensType?.merge_brand === 2) {
      selectedLensType.default_lens_color = colorItem.lens_color;
      if (selectedColorCategory) {
        selectedColorCategory.default_lens_color = colorItem.lens_color;
      }
    } else if (selectedBrand) {
      selectedBrand.default_lens_color = colorItem.lens_color;
    }
    onSelectedColorItem(colorItem);
    persistLensTypeTriple(selectedLensType, selectedBrand, colorItem);

    if (isWap) {
      SDLensTypePageColorPopUpClick(
        selectedLensType,
        colorItem,
        selectedBrand,
        selectedColorCategory?.title ?? '',
      );
    }
  };

  const onLensColorCategoryClick = (
    item: LensShowItem,
    category: LensColorCategory,
    colorItem?: ColorItem | null,
  ) => {
    const resolvedColorItem = colorItem ?? resolveLensColorCategoryClickColor(category);

    if (!resolvedColorItem) return;

    onSelectedBrand(null);
    setBrandExpand({ [getLensTypeKey(item)]: true });
    setSelectedColorCategory(category);
    item.default_category_title = category.title;
    category.default_lens_color = resolvedColorItem.lens_color;
    item.default_lens_color = resolvedColorItem.lens_color;
    onSelectedLensType(item);
    handleSetPrescriptionType(item);
    onSelectedColorItem(resolvedColorItem);
    persistLensTypeTriple(item, null, resolvedColorItem);
    handleSetShowChooseColor(true, item, resolvedColorItem, undefined, category.title);

    SDLensTypePageSelectClick(resolvedColorItem, item);
    SDLensTypePageBrandSelectClick(item, undefined, resolvedColorItem, category.title);
  };
  /*
   * 点击事件处理-----结束
   */

  /*
   * 选中状态判断————开始
   */
  // 检查颜色是否选中
  const checkColorSelected = (item: LensShowItem, brandItem: BrandItem, colorItem: ColorItem) => {
    let brandCheckResult = true;
    if (selectedLensType?.merge_brand === 1 && selectedBrand) {
      brandCheckResult = isSameLensBrand(brandItem, selectedBrand);
    }
    return (
      item?.lens_type === selectedLensType?.lens_type &&
      item?.lens_name === selectedLensType?.lens_name &&
      brandCheckResult &&
      selectedColorItem?.lens_color === colorItem?.lens_color
    );
  };

  const hasColorList = (item: LensShowItem | BrandItem) => {
    // 如果有颜色列表
    if (Array.isArray(item.color_list) && item.color_list.length) {
      // 颜色列表的长度为1，并且这个颜色为non 不展示
      return !(item.color_list.length === 1 && item.color_list[0].lens_color === LENS_COLOR.NON);
    }
    return false;
  };

  const isSameLensType = (item: LensShowItem) =>
    selectedLensType?.lens_type === item.lens_type &&
    selectedLensType?.lens_name === item.lens_name;

  // 获取镜片类型层的颜色列表，用于镜片类型层获取最低价格镜片。 （注意：颜色分组价格在一级镜片类型上）
  const getLensTypeColorList = (item: LensShowItem) =>
    shouldShowLensColorCategories(item)
      ? getFlattenedLensColorCategoryColors(item)
      : item.color_list;

  const getDefaultColorItem = (item: LensShowItem | BrandItem | LensColorCategory) => {
    if (!Array.isArray(item.color_list) || item.color_list.length === 0) {
      return null;
    }

    return (
      item.color_list.find((colorItem) => colorItem.lens_color === item.default_lens_color) ??
      item.color_list[0]
    );
  };

  const getLensTypePromotionDisplayInfo = (item: LensShowItem) => {
    const currentColorItem = isSameLensType(item) ? selectedColorItem : null;

    return resolveOptionPromotionDisplayInfo(item, currentColorItem ?? getDefaultColorItem(item));
  };

  const getBrandPromotionDisplayInfo = (brandItem: BrandItem) => {
    const isBrandSelected = isSameLensBrand(brandItem, selectedBrand);

    return resolveSelectablePromotionDisplayInfo(brandItem, selectedColorItem, isBrandSelected);
  };

  // 获取当前活跃的镜片数据源（品牌或类型）
  const getActiveSource = (): BrandItem | LensShowItem => {
    if (selectedLensType?.merge_brand === 1 && selectedBrand) {
      return selectedBrand;
    }
    return selectedLensType;
  };

  const getActiveColorList = () => {
    if (selectedColorCategory) {
      return selectedColorCategory.color_list;
    }

    return getActiveSource()?.color_list;
  };
  /*
   * 选中状态判断————结束
   */

  const showExplain = (item: any) => {
    setShowExplainPop(true);
    setExplainContent(item);
  };

  function SDLensTypePageSelectClick(colorItem?: ColorItem, lens?: LensShowItem) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice || isFrameProcess,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      /** 仅镜框配镜：单色折射率场景与镀膜页埋点同源，取颜色下默认镜片 id/code */
      const lensFromColor = colorItem?.lens_list?.[0];
      const resolvedPresType = getApiPrescriptionType(prescriptionType) ?? presTypeRef.current;
      const shouldReportLensSku = NON_PRESCRIPTION_TYPES.includes(resolvedPresType);
      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: lens,
        lensTypeForTracking: lens?.sd_lens_type || lens?.lens_type,
      });

      trackEvent(
        {
          eventName: 'LensTypePage_Select_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: lensTypeTrackingFields.lens_type_name,
            lens_type: lensTypeTrackingFields.lens_type,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
            ...(shouldReportLensSku
              ? {
                  lens_id: sensorParseString(lensFromColor?.id),
                  lens_code: sensorParseString(lensFromColor?.code),
                }
              : {}),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  function SDLensTypePageSelectExp(colorItem?: ColorItem, lens?: LensShowItem) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: lens,
        lensTypeForTracking: lens?.sd_lens_type || lens?.lens_type,
      });
      trackEvent(
        {
          eventName: 'LensTypePage_Select_Exp',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: lensTypeTrackingFields.lens_type_name,
            lens_type: lensTypeTrackingFields.lens_type,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  useEffect(() => {
    try {
      setTimeout(() => {
        trackEvent(
          {
            eventName: 'LensTypePage_View',
            properties: {
              ...(lensGoodsSensorsData || {}),
              prescription_type: presTypeRef.current,
              prescription_type_name: presTypeOptionNameRef.current,
            },
          },
          [ProviderType.SENSORS],
        );
      }, 500); // 延迟500ms. 1. 让相关数据就绪；2. 让 EditLensPage_View 事件先触发
    } catch (e) {
      logger.error('👀', e);
    }
  }, []);

  function SDLensTypePageBrandSelectClick(
    lens?: LensShowItem,
    brand?: BrandItem,
    colorItem?: ColorItem,
    lensColorCategoryName = '',
  ) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice || isFrameProcess,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      // 有的场景下折射率只能从colorItem中拿默认的
      const lensFromColor = colorItem?.lens_list?.[0];
      const resolvedPresType = getApiPrescriptionType(prescriptionType) ?? presTypeRef.current;
      const shouldReportLensSku = NON_PRESCRIPTION_TYPES.includes(resolvedPresType);
      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: lens,
        brand,
        lensTypeForTracking: brand?.lens_type || lens?.sd_lens_type || lens?.lens_type,
      });
      trackEvent(
        {
          eventName: 'LensTypePage_BrandSelect_Click',
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: lensTypeTrackingFields.lens_type_name,
            lens_type: lensTypeTrackingFields.lens_type,
            lens_brand_name: lensTypeTrackingFields.lens_brand_name,
            lens_brand: lensTypeTrackingFields.lens_brand,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
            lens_color_category_name: lensColorCategoryName,
            ...(shouldReportLensSku
              ? {
                  lens_id: sensorParseString(lensFromColor?.id),
                  lens_code: sensorParseString(lensFromColor?.code),
                }
              : {}),
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  function SDLensTypePageColorPopUpExp(
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
    lensColorCategoryName = '',
  ) {
    SDLensTypePageColorPopUpEvent(
      'LensTypePage_ColorPopUp_Exp',
      lens,
      colorItem,
      brand,
      lensColorCategoryName,
    );
  }

  function SDLensTypePageColorPopUpClick(
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
    lensColorCategoryName = '',
  ) {
    SDLensTypePageColorPopUpEvent(
      'LensTypePage_ColorPopUp_Click',
      lens,
      colorItem,
      brand,
      lensColorCategoryName,
    );
  }

  function SDLensTypePageColorPopUpEvent(
    eventName: string,
    lens: LensShowItem,
    colorItem: ColorItem,
    brand?: BrandItem,
    lensColorCategoryName = '',
  ) {
    try {
      let lens_color = '';
      let lens_price = null;
      let current_lens_price = null;
      let currencyName = '';
      if (colorItem) {
        lens_color = colorItem.color_name;
        const lensPriceInfo = handleColorItemPriceInfo(
          colorItem,
          hasIndexPrice,
          currency,
          userInfo,
        );
        lens_price = parseFloat(lensPriceInfo.price);
        current_lens_price = parseFloat(lensPriceInfo.sell_price);
        currencyName = currency.name;
      }
      const lensTypeTrackingFields = resolveLensTypeTrackingFields({
        lensType: lens,
        brand,
        lensTypeForTracking: brand?.lens_type || lens?.sd_lens_type || lens?.lens_type,
      });
      trackEvent(
        {
          eventName,
          properties: {
            ...(lensGoodsSensorsData || {}),
            prescription_type: presTypeRef.current,
            prescription_type_name: presTypeOptionNameRef.current,
            lens_type_name: lensTypeTrackingFields.lens_type_name,
            lens_type: lensTypeTrackingFields.lens_type,
            lens_brand: lensTypeTrackingFields.lens_brand,
            lens_brand_name: lensTypeTrackingFields.lens_brand_name,
            lens_color,
            lens_price,
            current_lens_price,
            currency: currencyName,
            lens_color_category_name: lensColorCategoryName,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error('👀', e);
    }
  }

  const checkLensTypeSelected = (item: LensShowItem) => {
    // 对于merge_brand === 2的情况，检查lens_type和lens_name是否匹配
    const isLensSelected =
      selectedLensType?.lens_type === item.lens_type &&
      selectedLensType?.lens_name === item.lens_name &&
      selectedLensType?.merge_brand === 2 &&
      !shouldShowLensColorCategories(item);

    // 大屏，只要选中父级就添加选中样式。
    const isMergedLensSelected =
      !isWap &&
      selectedLensType?.lens_type === item.lens_type &&
      selectedLensType?.lens_name === item.lens_name;

    return isLensSelected || isMergedLensSelected;
  };

  // PC wap 价格展示位置不同，没办法通过断点来复用
  const getResponsiveLensTypePrice = (item: LensShowItem, id?: string) => {
    const colorList = getLensTypeColorList(item);

    if (colorList?.length && item.merge_brand === 2) {
      const currentColorItem = isSameLensType(item) ? selectedColorItem : null;

      return (
        <div
          id={id}
          className={`${stepCommonClassName.priceContainer} ${shouldShowLensColorCategories(item) ? 'xl:!ml-[32px]' : ''}`}
        >
          <LensTypePrice
            colorItem={currentColorItem ?? undefined}
            colorList={currentColorItem ? undefined : colorList}
            hasIndexPrice={hasIndexPrice}
            renderPrice={(price, sellPrice) =>
              Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                <span className={stepCommonClassName.priceInnerBox}>
                  <AccessiblePrice
                    value={sellPrice}
                    label={tPriceA11y('srCurrent')}
                    visualClassName={`${stepCommonClassName.priceText} ${
                      price !== sellPrice ? 'sale' : ''
                    }`}
                  />
                  <AccessiblePrice
                    value={price}
                    label={tPriceA11y('srOriginal')}
                    visualClassName={stepCommonClassName.originalPriceText(waiMode)}
                  />
                </span>
              ) : (
                <AccessiblePrice
                  value={t('free')}
                  label={tPriceA11y('srCurrent')}
                  visualClassName={`${stepCommonClassName.priceText} ${
                    price !== sellPrice ? 'sale' : ''
                  }`}
                />
              )
            }
          />
        </div>
      );
    }
    return null;
  };
  const [lensTypeContainer, setLensTypeContainer] = useState<HTMLElement>(null);

  // 记录已曝光的镜片类型项目，防止重复曝光
  const exposedItemsRef = useRef<Set<string>>(new Set());

  /** 默认/恢复选中后滚入视野：品牌折叠类型滚 color 所属品牌整块居中，否则滚类型行 */
  const scrollSelectedLensTypeIntoView = () => {
    const root = document.querySelector('#lens-process-type');
    if (!root || !selectedLensType || !selectedColorItem || !lensTypeColorData?.length) return;

    const rowIndex = lensTypeColorData.findIndex(
      (item) =>
        item.lens_type === selectedLensType.lens_type &&
        item.lens_name === selectedLensType.lens_name,
    );
    if (rowIndex < 0) return;

    const typeRow = root.querySelector<HTMLElement>(`[data-lens-type-row="${rowIndex}"]`);
    if (!typeRow) return;

    const brandTitle = selectedBrand?.brand_title;
    let scrollTarget: HTMLElement = typeRow;
    if (brandTitle) {
      const brandBlock = typeRow.querySelector<HTMLElement>(
        `[data-lens-brand-title="${brandTitle}"]`,
      );
      if (brandBlock) scrollTarget = brandBlock;
    }

    scrollTarget.scrollIntoView({
      block: 'center',
      behavior: 'smooth',
      inline: 'nearest',
    });
  };

  useLayoutEffect(() => {
    if (!selectedLensType || !selectedColorItem) return;
    // const timer = window.setTimeout(scrollSelectedLensTypeIntoView, 100);
    // return () => window.clearTimeout(timer);
    scrollSelectedLensTypeIntoView();
  }, [
    selectedLensType?.lens_type,
    selectedLensType?.lens_name,
    selectedBrand?.brand_title,
    selectedColorItem?.lens_color,
    lensTypeColorData?.length,
  ]);

  useEffect(() => {
    setLensTypeContainer(document.querySelector('#lens-process-type') as HTMLElement);
  }, []);

  // 组件卸载时清空曝光记录，支持重新进入时再次曝光
  useEffect(() => {
    return () => {
      exposedItemsRef.current.clear();
    };
  }, []);
  const getResponsiveBrandTypePrice = (brandItem: BrandItem, id?: string) => {
    if (Array.isArray(brandItem.color_list) && brandItem.color_list.length) {
      const brandPriceColorSource = resolveBrandPriceColorSource(
        brandItem,
        selectedBrand,
        selectedColorItem,
      );

      return (
        <div id={id} className={stepCommonClassName.priceContainer}>
          <LensTypePrice
            colorItem={brandPriceColorSource.colorItem}
            colorList={brandPriceColorSource.colorList}
            hasIndexPrice={hasIndexPrice}
            renderPrice={(price, sellPrice) =>
              Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                <span className={stepCommonClassName.priceInnerBox}>
                  <AccessiblePrice
                    value={sellPrice}
                    label={tPriceA11y('srCurrent')}
                    visualClassName={`${stepCommonClassName.priceText} ${
                      price !== sellPrice ? 'sale' : ''
                    }`}
                  />
                  <AccessiblePrice
                    value={price}
                    label={tPriceA11y('srOriginal')}
                    visualClassName={stepCommonClassName.originalPriceText(waiMode)}
                  />
                </span>
              ) : (
                <AccessiblePrice
                  value={t('free')}
                  label={tPriceA11y('srCurrent')}
                  visualClassName={`${stepCommonClassName.priceText} ${price !== sellPrice ? 'sale' : ''}`}
                />
              )
            }
          />
        </div>
      );
    }
    return null;
  };

  const renderColorOptions = ({
    colorList,
    idPrefix,
    name,
    labelledBy,
    isSelected,
    onSelect,
    className = '',
    itemClassName = '',
    imageSize = 16,
    tooltipTrigger,
    brandTitle,
  }: {
    colorList?: ColorItem[] | null;
    idPrefix: string;
    name: string;
    labelledBy: string;
    isSelected: (colorItem: ColorItem) => boolean;
    onSelect: (colorItem: ColorItem) => void;
    className?: string;
    itemClassName?: string;
    imageSize?: number;
    tooltipTrigger?: 'hover' | 'click';
    brandTitle?: string;
  }) => {
    if (!Array.isArray(colorList) || !colorList.length) return null;

    return (
      <div role={tooltipTrigger === 'click' ? 'group' : 'radiogroup'} aria-labelledby={labelledBy}>
        <ul className={`${stepCommonClassName.colorSelector} ${className}`}>
          {colorList.map((colorItem, colorIndex) => {
            const isColorSelected = isSelected(colorItem);

            return (
              <li
                key={`${colorItem.lens_color}-${colorIndex}`}
                data-lens-color={colorItem.lens_color}
                data-brand-title={brandTitle}
                className={`${stepCommonClassName.colorItem} relative focus-within:ring-2 focus-within:ring-primary-purple focus-within:ring-offset-2 ${
                  isColorSelected
                    ? stepCommonClassName.selectedColorItem
                    : stepCommonClassName.unselectedColorItem
                } ${itemClassName}`}
                style={colorItem.lens_color === LENS_COLOR.NON ? { display: 'none' } : {}}
              >
                <LensColorRadio
                  colorItem={colorItem}
                  checked={isColorSelected}
                  id={`${idPrefix}-${colorIndex}`}
                  name={name}
                  imageSize={imageSize}
                  onSelect={() => onSelect(colorItem)}
                  tooltipTrigger={tooltipTrigger}
                />
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const renderLensColorOptions = (
    item: LensShowItem,
    idPrefix: string,
    name: string,
    labelledBy: string,
  ) =>
    renderColorOptions({
      colorList: item.color_list,
      idPrefix,
      name,
      labelledBy,
      tooltipTrigger: isWap ? undefined : 'hover',
      isSelected: (colorItem) => checkColorSelected(item, null, colorItem),
      onSelect: (colorItem) => onLensColorClick(item, colorItem),
    });

  const renderBrandColorOptions = (
    brandItem: BrandItem,
    item: LensShowItem,
    idPrefix: string,
    name: string,
    labelledBy: string,
  ) =>
    renderColorOptions({
      colorList: brandItem.color_list,
      idPrefix,
      name,
      labelledBy,
      tooltipTrigger: isWap ? undefined : 'hover',
      isSelected: (colorItem) => checkColorSelected(item, brandItem, colorItem),
      onSelect: (colorItem) => onLensBrandColorClick(colorItem, brandItem, item),
      brandTitle: brandItem.brand_title,
    });

  const renderLensColorCategories = (
    item: LensShowItem,
    groupId: string,
    optionId: string,
    labelledBy: string,
  ) => {
    const categories = getLensColorCategoryViewModels(item);

    if (categories.length <= 1 || !isSameLensType(item) || !brandExpand[getLensTypeKey(item)]) {
      return null;
    }

    return (
      <div
        id={groupId}
        className={stepCommonClassName.colorCategoryBox}
        role="group"
        aria-labelledby={labelledBy}
      >
        {categories.map((category, categoryIndex) => {
          const isSelectedCategory = selectedColorCategory?.title === category.title;
          const categoryId = `${optionId}-category-${categoryIndex}`;
          const categoryTitleId = `${categoryId}-title`;

          return (
            <div
              key={`${category.id ?? category.title}`}
              className={`${stepCommonClassName.colorCategoryItem} relative ${
                isSelectedCategory ? stepCommonClassName.selectedColorCategoryItem : ''
              }`}
            >
              <button
                type="button"
                className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer border-0 bg-transparent p-0"
                aria-pressed={isSelectedCategory}
                aria-labelledby={categoryTitleId}
                onClick={() => onLensColorCategoryClick(item, category)}
              />
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 z-[1] rounded-[5px] peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-primary-purple"
              />
              <div className="relative z-[2] pointer-events-none">
                <p id={categoryTitleId} className={stepCommonClassName.colorCategoryTitle}>
                  {category.title}
                </p>
                <div className="pointer-events-auto relative z-[3]">
                  {renderColorOptions({
                    colorList: category.color_list,
                    idPrefix: `${categoryId}-color`,
                    name: `${optionId}-category-colors`,
                    labelledBy: categoryTitleId,
                    tooltipTrigger: isWap ? undefined : 'hover',
                    isSelected: (colorItem) => checkColorSelected(item, null, colorItem),
                    onSelect: (colorItem) => onLensColorCategoryClick(item, category, colorItem),
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const activeColorList = getActiveColorList();

  return (
    <div id="lens-process-type" className="w-full h-full box-border overflow-y-hidden font-br">
      {/* <InfiniteScroll className="my-scroll" onScroll={handleScroll}> */}
      <h3 id={lensTypeGroupLabelId} className={stepCommonClassName.stepTitle}>
        <span>{lensTypeTitle}</span>
      </h3>
      {(() => {
        // 加载中显示骨架屏
        if (isLoadingLensShow) {
          return (
            <div className={stepCommonClassName.optionsContainer}>
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className={stepCommonClassName.optionItem}>
                  <div className={stepCommonClassName.optionBox}>
                    <div className={stepCommonClassName.optionContent}>
                      {/* 左侧图片骨架屏 */}
                      <div className={`${stepCommonClassName.imageContainer} min-w-[60px]`}>
                        <Skeleton variant="rectangular" width={60} height={60} animation="wave" />
                      </div>
                      {/* 右侧内容骨架屏 */}
                      <div className={stepCommonClassName.textContainer}>
                        <div className={stepCommonClassName.titleContainer}>
                          <Skeleton variant="text" width="60%" height={20} animation="wave" />
                        </div>
                        <div className={stepCommonClassName.priceContainer}>
                          <Skeleton variant="text" width="40%" height={16} animation="wave" />
                        </div>
                        <div className={stepCommonClassName.colorSelector}>
                          {Array.from({ length: 4 }).map((__, colorIndex) => (
                            <Skeleton
                              key={colorIndex}
                              variant="circular"
                              width={22}
                              height={22}
                              animation="wave"
                              style={{ marginRight: 12, marginBottom: 5 }}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        }

        // 有数据时显示列表
        if (Array.isArray(lensTypeColorData) && lensTypeColorData.length) {
          return (
            <div role="radiogroup" aria-labelledby={lensTypeGroupLabelId}>
              <ul className={stepCommonClassName.optionsContainer}>
                {lensTypeColorData.map((item, index) => {
                  const optionId = `${lensTypeOptionIdPrefix}-${index}`;
                  const optionTitleId = `${optionId}-title`;
                  const optionDescriptionId = `${optionId}-description`;
                  const optionPriceId = `${optionId}-price`;
                  const optionPromotionId = `${optionId}-promotion`;
                  const recommendBadgeId = `${optionId}-recommend`;
                  const brandGroupId = `${optionId}-brands`;
                  const colorCategoryGroupId = `${optionId}-color-categories`;
                  const typeColorRadioName = `${optionId}-colors`;
                  const isTypeSelected = isSameLensType(item);
                  const isSecondaryExpanded = !!brandExpand[getLensTypeKey(item)];
                  const hasColorCategories = shouldShowLensColorCategories(item);
                  const hasExpandableOptions = item.merge_brand === 1 || hasColorCategories;
                  const showDescription = Boolean(
                    item.description &&
                      (!isWap ||
                        (!hasColorCategories &&
                          (item.color_list?.length ?? 0) <= 1 &&
                          (item.merge_brand === 1 ? isSecondaryExpanded : isTypeSelected))),
                  );
                  const typePromotionInfo =
                    item.merge_brand === 1
                      ? resolvePromotionDisplayInfo(item)
                      : getLensTypePromotionDisplayInfo(item);
                  const showTypePromotion = Boolean(
                    typePromotionInfo.promotionDisplayLabel &&
                      (item.merge_brand !== 1 || !isSecondaryExpanded),
                  );
                  const hasTypePrice = Boolean(
                    getLensTypeColorList(item)?.length && item.merge_brand === 2,
                  );
                  const optionDescribedBy = [
                    item.description ? optionDescriptionId : '',
                    hasTypePrice ? optionPriceId : '',
                    showTypePromotion ? optionPromotionId : '',
                    item.recommend === 1 ? recommendBadgeId : '',
                  ]
                    .filter(Boolean)
                    .join(' ');

                  return (
                    <li
                      key={index}
                      data-lens-type-row={index}
                      className={`${stepCommonClassName.optionItem} ${item.recommend === 1 ? stepCommonClassName.recommendedItem : ''}`}
                    >
                      {/* 曝光追踪组件 */}
                      <LensTypeExpTracker
                        item={item}
                        onExposure={SDLensTypePageSelectExp}
                        exposedItems={exposedItemsRef}
                      />
                      {item.recommend === 1 && (
                        <div id={recommendBadgeId} className={stepCommonClassName.recommendBadge}>
                          <div className={stepCommonClassName.recommendBadgeContent}>
                            <span>{recommendedTitle}</span>
                          </div>
                        </div>
                      )}
                      <div
                        className={`lens-type-option-${index} scroll-mt-[5px] ${stepCommonClassName.optionBox} ${
                          checkLensTypeSelected(item) ? stepCommonClassName.selectedOptionBox : ''
                        } cursor-pointer`}
                      >
                        {showTypePromotion && item.merge_brand === 1 && (
                          <LensTypePrice
                            colorList={
                              item.brand_list?.find((brandItem) => brandItem.brand_title === 'non')
                                ?.color_list || null
                            }
                            hasIndexPrice={false}
                            renderPrice={() => (
                              <div
                                id={optionPromotionId}
                                className={stepCommonClassName.discountBadge(
                                  typePromotionInfo.applyMembershipLevel,
                                )}
                              >
                                <span className="relative top-[1px]">
                                  {typePromotionInfo.promotionDisplayLabel}
                                </span>
                              </div>
                            )}
                          />
                        )}
                        <div
                          className={`${stepCommonClassName.optionContent} relative p-[20px_20px_20px_14px]`}
                        >
                          <input
                            id={optionId}
                            className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer opacity-0"
                            type="radio"
                            name={lensTypeRadioName}
                            checked={isTypeSelected}
                            onChange={() => onLensClick(item, index)}
                            onClick={() => {
                              if (isTypeSelected) onLensClick(item, index);
                            }}
                            aria-labelledby={optionTitleId}
                            aria-describedby={optionDescribedBy || undefined}
                            aria-controls={
                              hasExpandableOptions && isSecondaryExpanded
                                ? item.merge_brand === 1
                                  ? brandGroupId
                                  : colorCategoryGroupId
                                : undefined
                            }
                          />
                          <span
                            aria-hidden
                            className="pointer-events-none absolute inset-0 z-[1] rounded-[6px] peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-primary-purple"
                          />
                          <div
                            className={`${stepCommonClassName.imageContainer} relative z-[2] min-w-[60px] pointer-events-none`}
                          >
                            <Image
                              src={item.img}
                              alt=""
                              width={60}
                              height={60}
                              className="object-contain w-[60px] h-[60px]"
                            />
                          </div>
                          <div
                            className={`${stepCommonClassName.textContainer} z-[2] pointer-events-none ${
                              item.merge_brand === 1
                                ? 'relative min-h-[50px] flex flex-col items-start justify-center'
                                : ''
                            } ${shouldShowLensColorCategories(item) ? 'flex justify-between' : ''}`}
                          >
                            {!isWap && item.merge_brand === 1 && (
                              <button
                                type="button"
                                className={`${stepCommonClassName.lensTypeSecondaryArrow} pointer-events-auto z-[3] border-0 bg-transparent p-0 ${
                                  !isSecondaryExpanded ? '' : 'rotate-180 '
                                }`}
                                aria-label={item.lens_name}
                                aria-expanded={isSecondaryExpanded}
                                aria-controls={isSecondaryExpanded ? brandGroupId : undefined}
                                onClick={() => onLensClick(item, index)}
                              >
                                <CollapseArrowDownOutlined
                                  className={stepCommonClassName.arrowIcon}
                                  aria-hidden
                                />
                              </button>
                            )}

                            <div className={stepCommonClassName.textCenterContainer}>
                              <div className={stepCommonClassName.titleContainer}>
                                {item.type_img_m ? (
                                  <>
                                    <Image
                                      src={item.type_img_m}
                                      className={stepCommonClassName.titleImage}
                                      width={192}
                                      height={21}
                                      alt=""
                                    />
                                    <span id={optionTitleId} className="sr-only">
                                      {item.lens_name}
                                    </span>
                                  </>
                                ) : (
                                  <p id={optionTitleId}>{item.lens_name}</p>
                                )}
                                {item.merge_brand === 1 && isWap && (
                                  <button
                                    type="button"
                                    className={`${stepCommonClassName.lensTypeSecondaryArrowMobile} pointer-events-auto z-[3] border-0 bg-transparent p-0 ${
                                      !isSecondaryExpanded ? '' : 'rotate-180 '
                                    }`}
                                    aria-label={item.lens_name}
                                    aria-expanded={isSecondaryExpanded}
                                    aria-controls={isSecondaryExpanded ? brandGroupId : undefined}
                                    onClick={() => onLensClick(item, index)}
                                  >
                                    <CollapseArrowDownOutlined
                                      className={stepCommonClassName.arrowIcon}
                                      aria-hidden
                                    />
                                  </button>
                                )}
                                {item.explain && (
                                  <Tooltip
                                    title={
                                      <p
                                        className="text-[14px]"
                                        dangerouslySetInnerHTML={{
                                          __html: processLineBreaks(item.explain),
                                        }}
                                      />
                                    }
                                    describeChild
                                    placement="bottom"
                                    variant="light"
                                    sx={{
                                      zIndex: 1500,
                                      '& .MuiTooltip-tooltip': {
                                        fontSize: '14px',
                                        maxWidth: '490px',
                                      },
                                    }}
                                  >
                                    <QuestionResponsive
                                      className={stepCommonClassName.questionIcon}
                                      buttonClassName="pointer-events-auto relative z-[3]"
                                      ariaLabel={`${t('learnAboutDifferentLensUsages')}: ${
                                        item.lens_name || t('lensType')
                                      }`}
                                      onClick={() => isWap && showExplain(item)}
                                    />
                                  </Tooltip>
                                )}
                                {!isWap && item.option_img_pc && (
                                  <Image
                                    className="w-[auto] h-[16px] ml-[8px]"
                                    src={item.option_img_pc}
                                    width={102}
                                    height={16}
                                    alt=""
                                  />
                                )}
                              </div>
                              {/* TODO 动态图片如何设置宽高 */}
                              {isWap && item.option_img_m && (
                                <Image
                                  className="w-[auto] h-[16px] mt-[9px]"
                                  src={item.option_img_m}
                                  width={102}
                                  height={16}
                                  alt=""
                                />
                              )}

                              {item.description && !showDescription && (
                                <span id={optionDescriptionId} className="sr-only">
                                  {item.description}
                                </span>
                              )}
                              {showDescription && (
                                <p
                                  id={optionDescriptionId}
                                  className={`${stepCommonClassName.description(waiMode)} max-xl:pr-[0px]`}
                                >
                                  {item.description}
                                </p>
                              )}
                              {isWap && getResponsiveLensTypePrice(item, optionPriceId)}
                              {hasColorList(item) &&
                                item.merge_brand === 2 &&
                                !hasColorCategories && (
                                  <div className="pointer-events-auto relative z-[3]">
                                    {renderLensColorOptions(
                                      item,
                                      `${optionId}-color`,
                                      typeColorRadioName,
                                      optionTitleId,
                                    )}
                                  </div>
                                )}
                            </div>

                            {!isWap && getResponsiveLensTypePrice(item, optionPriceId)}
                            {hasColorCategories && (
                              <button
                                type="button"
                                className={`pointer-events-auto relative z-[3] self-center border-0 bg-transparent p-0 transition-transform duration-200 text-gray f16 xl:ml-16 ${
                                  !isSecondaryExpanded ? '' : 'rotate-180 '
                                }`}
                                aria-label={item.lens_name}
                                aria-expanded={isSecondaryExpanded}
                                aria-controls={
                                  isSecondaryExpanded ? colorCategoryGroupId : undefined
                                }
                                onClick={() => onLensClick(item, index)}
                              >
                                <CollapseArrowDownOutlined
                                  className={stepCommonClassName.arrowIcon}
                                  aria-hidden
                                />
                              </button>
                            )}
                            {showTypePromotion && item.merge_brand === 2 && (
                              <LensTypePrice
                                colorList={getLensTypeColorList(item)}
                                hasIndexPrice={hasIndexPrice}
                                renderPrice={() => (
                                  <div
                                    id={optionPromotionId}
                                    className={stepCommonClassName.discountBadge(
                                      typePromotionInfo.applyMembershipLevel,
                                    )}
                                  >
                                    <span className="relative top-[1px]">
                                      {typePromotionInfo.promotionDisplayLabel}
                                    </span>
                                  </div>
                                )}
                              />
                            )}
                          </div>
                          {hasColorList(item) &&
                            item.merge_brand === 2 &&
                            isWap &&
                            !shouldShowLensColorCategories(item) && (
                              <div className="pt-[17px] pl-[3px]">
                                <RightArrowOutlined
                                  className={stepCommonClassName.arrowIcon}
                                  aria-hidden
                                />
                              </div>
                            )}
                        </div>

                        {/* 品牌列表 */}
                        {item.merge_brand === 1 &&
                          item.brand_list?.length > 0 &&
                          isTypeSelected &&
                          isSecondaryExpanded && (
                            <div
                              id={brandGroupId}
                              className={stepCommonClassName.brandBox}
                              role="group"
                              aria-labelledby={optionTitleId}
                            >
                              {item.brand_list.map((brandItem, brandIndex) => {
                                const brandOptionId = `${optionId}-brand-${brandIndex}`;
                                const brandTitleId = `${brandOptionId}-title`;
                                const brandDescriptionId = `${brandOptionId}-description`;
                                const brandPriceId = `${brandOptionId}-price`;
                                const brandPromotionId = `${brandOptionId}-promotion`;
                                const brandColorRadioName = `${brandOptionId}-colors`;
                                const brandAccessibleName =
                                  [brandItem.brand_name, brandItem.brand_title]
                                    .filter(Boolean)
                                    .join(', ') || t('lensType');
                                const isBrandSelected = isSameLensBrand(brandItem, selectedBrand);
                                const brandPromotionInfo = getBrandPromotionDisplayInfo(brandItem);
                                const hasBrandPrice = Boolean(brandItem.color_list?.length);
                                const brandDescribedBy = [
                                  brandItem.description ? brandDescriptionId : '',
                                  hasBrandPrice ? brandPriceId : '',
                                  brandPromotionInfo.promotionDisplayLabel ? brandPromotionId : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ');

                                return (
                                  <div
                                    key={brandItem.brand_name}
                                    data-lens-brand-title={brandItem.brand_title}
                                    className={`${stepCommonClassName.brandItem} relative ${
                                      isBrandSelected
                                        ? stepCommonClassName.selectedBrandOptionBox
                                        : 'border-transparent'
                                    } cursor-pointer`}
                                  >
                                    <button
                                      type="button"
                                      className="peer absolute inset-0 z-[1] m-0 h-full w-full cursor-pointer border-0 bg-transparent p-0"
                                      aria-pressed={isBrandSelected}
                                      aria-labelledby={brandTitleId}
                                      aria-describedby={brandDescribedBy || undefined}
                                      onClick={() => onLensBrandClick(brandItem, item)}
                                    />
                                    <span
                                      aria-hidden
                                      className="pointer-events-none absolute inset-0 z-[1] rounded-[5px] peer-focus-visible:ring-2 peer-focus-visible:ring-inset peer-focus-visible:ring-primary-purple"
                                    />

                                    <div
                                      className={`${stepCommonClassName.brandItemInner} relative z-[2] pointer-events-none`}
                                    >
                                      <div
                                        className={`${stepCommonClassName.titleContainer} flex-wrap gap-[4px]`}
                                      >
                                        {brandItem.brand_img_m ? (
                                          <Image
                                            src={brandItem.brand_img_m}
                                            width={130}
                                            height={21}
                                            className="w-[auto] h-[21px]"
                                            alt=""
                                          />
                                        ) : (
                                          <p aria-hidden>{brandItem.brand_name}</p>
                                        )}
                                        <span id={brandTitleId} className="sr-only">
                                          {brandAccessibleName}
                                        </span>
                                        {brandItem.explain && (
                                          <Tooltip
                                            title={
                                              <p
                                                className="text-[14px]"
                                                dangerouslySetInnerHTML={{
                                                  __html: processLineBreaks(brandItem.explain),
                                                }}
                                              />
                                            }
                                            describeChild
                                            placement="bottom"
                                            variant="light"
                                            sx={{
                                              zIndex: 1500,
                                              '& .MuiTooltip-tooltip': {
                                                fontSize: '14px',
                                                maxWidth: '490px',
                                              },
                                            }}
                                          >
                                            <QuestionResponsive
                                              className={`${stepCommonClassName.questionIcon} !ml-[0px]`}
                                              buttonClassName="pointer-events-auto relative z-[3]"
                                              ariaLabel={`${t(
                                                'learnAboutDifferentLensUsages',
                                              )}: ${brandAccessibleName}`}
                                              onClick={() => isWap && showExplain(brandItem)}
                                            />
                                          </Tooltip>
                                        )}
                                        {isWap && (brandItem as any).option_img_m && (
                                          <Image
                                            className="w-[auto] h-[16px] ml-[5px] xl:ml-[8px]"
                                            width={102}
                                            height={16}
                                            src={(brandItem as any).option_img_m}
                                            alt=""
                                          />
                                        )}
                                        {!isWap && (brandItem as any).option_img_pc && (
                                          <Image
                                            className="w-[auto] h-[16px] ml-[5px] xl:ml-[8px]"
                                            width={102}
                                            height={16}
                                            src={(brandItem as any).option_img_pc}
                                            alt=""
                                          />
                                        )}

                                        {brandPromotionInfo.promotionDisplayLabel && (
                                          <LensTypePrice
                                            colorList={brandItem.color_list}
                                            hasIndexPrice={hasIndexPrice}
                                            renderPrice={() => (
                                              <div
                                                id={brandPromotionId}
                                                className={`${stepCommonClassName.discountBadge(
                                                  brandPromotionInfo.applyMembershipLevel,
                                                )} !rounded-[4px] static xl:ml-[4px]`}
                                              >
                                                <span className="relative top-[1px]">
                                                  {brandPromotionInfo.promotionDisplayLabel}
                                                </span>
                                              </div>
                                            )}
                                          />
                                        )}

                                        {hasColorList(brandItem) && isWap && (
                                          <div className="absolute right-0 top-0 text-auxiliary-black">
                                            <RightArrowOutlined
                                              className={stepCommonClassName.arrowIcon}
                                              aria-hidden
                                            />
                                          </div>
                                        )}
                                      </div>

                                      {brandItem.description && (
                                        <p
                                          id={brandDescriptionId}
                                          className={`${stepCommonClassName.description(waiMode)} pr-[10px]`}
                                        >
                                          {brandItem.description}
                                        </p>
                                      )}

                                      {isWap &&
                                        getResponsiveBrandTypePrice(brandItem, brandPriceId)}

                                      {hasColorList(brandItem) && (
                                        <div className="pointer-events-auto relative z-[3]">
                                          {renderBrandColorOptions(
                                            brandItem,
                                            item,
                                            `${brandOptionId}-color`,
                                            brandColorRadioName,
                                            brandTitleId,
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {!isWap && getResponsiveBrandTypePrice(brandItem, brandPriceId)}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        {/* 镜片类型颜色分组 */}
                        {item.merge_brand === 2 &&
                          hasColorCategories &&
                          renderLensColorCategories(
                            item,
                            colorCategoryGroupId,
                            optionId,
                            optionTitleId,
                          )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        }

        // 无数据时显示空状态
        return (
          <div className={stepCommonClassName.emptyState}>
            <Image
              src="/lens-process/null.png"
              alt=""
              width={118}
              height={118}
              className="xl:w-[168px] xl:h-[134px]"
            />
            <p className={stepCommonClassName.emptyStateText}>
              {t('sorryNoLensesAvailable')}
              {!hasIndexPrice && (
                <>
                  <br />
                  {t('pleaseEditYourPrescription')}
                </>
              )}
            </p>
            {!hasIndexPrice && (
              <Button
                variant="contained"
                className={stepCommonClassName.emptyStateButton}
                onClick={onBackToEditPrescription}
              >
                {t('editPrescription')}
              </Button>
            )}
          </div>
        );
      })()}
      {/* </InfiniteScroll> */}

      {/* 镜片说明弹窗 */}
      <ExplainDialog
        title={explainContent.brand_name || explainContent.lens_name}
        open={showExplainPop}
        onClose={() => setShowExplainPop(false)}
      >
        <p
          className="text-sm font-br"
          dangerouslySetInnerHTML={{
            __html: processLineBreaks(explainContent.explain),
          }}
        />
      </ExplainDialog>

      {/* 底部选择镜片颜色弹窗 */}
      {lensTypeContainer && (
        <Popup
          open={showChooseColor}
          onClose={() => setShowChooseColor(false)}
          childrenMaxHeight="80vh"
          position="bottom"
          className="font-br"
          bodyClassName="!p-[22px_15px_69px]"
          // TODO 这里挂载到body上，在移动端多测试几种机型，确保不会挡住底部按钮
          container={lensTypeContainer}
          round
          zIndex={85}
        >
          {/* TODO 加了这么多东西，仍然无法阻止弹层滚动时带动页面滚动 */}
          <div
            className="relative text-sm text-primary-black box-border overflow-hidden touch-none isolate"
            style={{
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            }}
            onTouchMove={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onScroll={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <button
              type="button"
              className="absolute top-[0] right-[0] cursor-pointer border-0 bg-transparent p-0"
              onClick={() => setShowChooseColor(false)}
              aria-label="Close color options"
            >
              <CloseOutlined className="f24 font-bold text-primary-black" aria-hidden />
            </button>
            <p id={colorPopupTitleId} className="text-center font-bb text-[16px]">
              {(() => {
                const activeSource = getActiveSource();
                if ((activeSource as BrandItem)?.brand_img_m) {
                  return (
                    <>
                      <Image
                        className="inline-block w-[auto] h-[21px]"
                        src={(activeSource as BrandItem).brand_img_m}
                        alt=""
                        width={130}
                        height={21}
                      />
                      <span className="sr-only">
                        {(activeSource as BrandItem).brand_name ||
                          (activeSource as BrandItem).brand_title ||
                          selectedLensType?.lens_name}
                      </span>
                    </>
                  );
                }
                if ((activeSource as BrandItem)?.brand_name) {
                  return <span>{(activeSource as BrandItem).brand_name}</span>;
                }
                return <span>{(activeSource as LensShowItem)?.lens_name}</span>;
              })()}
            </p>
            <div className="max-h-[500px] overflow-y-auto" onScroll={(e) => e.stopPropagation()}>
              <div className="mt-[20px] flex items-center justify-center">
                {selectedColorItem && (
                  <ProductImageWithLenses
                    className="!w-[196px] h-[98px]"
                    imgUrl={lensGoodsItem?.product?.img || ''}
                    product={{
                      background_image_of_mirror_leg:
                        lensGoodsItem?.product?.image?.background_image_of_mirror_leg?.[0] || '',
                      frame_background_image:
                        lensGoodsItem?.product?.image?.frame_background_image?.[0] || '',
                    }}
                    lens={selectedColorItem}
                  />
                )}
              </div>
              {/* 使用getActiveSource获取当前活跃的数据源 */}
              {getActiveSource()?.description && (
                <p className="p-[25px_20px_27px] text-xs text-primary-black">
                  {getActiveSource().description}
                </p>
              )}
              {activeColorList?.length && (
                <div className="">
                  <LensTypePrice
                    colorItem={selectedColorItem}
                    hasIndexPrice={hasIndexPrice}
                    renderPrice={(price, sellPrice) =>
                      Number(sellPrice?.replace(/[^0-9.]/g, '')) > 0 ? (
                        <span className="flex flex-col text-center">
                          <AccessiblePrice
                            value={sellPrice}
                            label={tPriceA11y('srCurrent')}
                            visualClassName={`${stepCommonClassName.priceText} f18`}
                          />
                          {price !== sellPrice && (
                            <AccessiblePrice
                              value={price}
                              label={tPriceA11y('srOriginal')}
                              visualClassName={`${stepCommonClassName.originalPriceText(waiMode)} f12 pt-[8px]`}
                            />
                          )}
                        </span>
                      ) : (
                        <AccessiblePrice
                          value={t('free')}
                          label={tPriceA11y('srCurrent')}
                          visualClassName={`${stepCommonClassName.priceText} f18`}
                        />
                      )
                    }
                  />
                </div>
              )}
              {renderColorOptions({
                colorList: activeColorList,
                idPrefix: `${lensTypeOptionIdPrefix}-popup-color`,
                name: popupColorRadioName,
                labelledBy: colorPopupTitleId,
                className: '!justify-center pt-[45px] pb-[10px]',
                itemClassName: 'mx-[14px] mb-[24px] h-[34px] w-[34px]',
                imageSize: 28,
                tooltipTrigger: 'click',
                isSelected: (colorItem) =>
                  checkColorSelected(selectedLensType, selectedBrand, colorItem),
                onSelect: onPopupLensColorClick,
              })}
            </div>
          </div>
        </Popup>
      )}
    </div>
  );
};

export default LensType;
